import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { type FC, useMemo, useState } from 'react';
import { type FunnelStatus } from 'src/api/generated';
import { AiBadge } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import { classes } from 'src/modules/shared/utils/styles.util';
import { CountBadge } from 'src/modules/shared/components/atoms/count-badge';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import type { DropdownItem } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { CreateAutomationFromTemplateModal } from 'src/modules/automations/components/molecules/templates/create-automation-from-template-modal';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  AutomationWorkflowPermission,
  AutomationWorkflowTriggerPermission,
  TemplateAppPermission,
} from 'src/modules/shared/types/permissions.type';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  PiArrowsInLineHorizontal,
  PiCirclesThree,
  PiDotsThreeVertical,
} from 'react-icons/pi';

interface StatusHeaderProps {
  status: FunnelStatus;
  count: number;
  countLoading?: boolean;
  className?: string;
  isAiLinked?: boolean;
  onToggleCollapsed?: () => void;
}

export const PipelineStatusColumnHeader: FC<StatusHeaderProps> = ({
  status,
  count,
  countLoading = false,
  className = '',
  isAiLinked = false,
  onToggleCollapsed,
}) => {
  const [isAutomationModalOpen, setAutomationModalOpen] = useState(false);
  const { hasPermission: canCreateAutomation, loading: loadingAutomationPerm } =
    useHasPermissions([
      TemplateAppPermission.Read,
      AutomationWorkflowPermission.Create,
      AutomationWorkflowTriggerPermission.Create,
    ]);
  const { t } = useI18n();

  const quickAutomationContext = useMemo(
    () => ({
      funnelId: status.funnelId,
      statusId: status.id,
      statusName: status.name,
    }),
    [status.funnelId, status.id, status.name]
  );
  const presetMapping = useMemo(
    () => ({
      funnelStatusId: {
        kind: 'literal' as const,
        literalType: 'number' as const,
        value: status.id.toString(),
      },
    }),
    [status.id]
  );
  const menuItems: DropdownItem[] = [];

  if (onToggleCollapsed) {
    menuItems.push({
      onClick: onToggleCollapsed,
      icon: <PiArrowsInLineHorizontal className="w-4 h-4 text-secondary-500" />,
      content: t('communications.pipeline.actions.collapseColumn'),
    });
  }

  if (canCreateAutomation) {
    menuItems.push({
      onClick: () => setAutomationModalOpen(true),
      disabled: loadingAutomationPerm,
      icon: <PiCirclesThree className="w-4 h-4 text-primary-600" />,
      content: t('communications.pipeline.actions.quickAutomation'),
    });
  }

  return (
    <>
      <div
        className={classes(
          'flex items-center justify-between px-2.5 py-3',
          className
        )}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center">
            {isAiLinked ? (
              <AiBadge size="md" appearance="filled" variant="neutral" />
            ) : (
              <div className="w-1.5 h-1.5 rounded-full bg-secondary-300" />
            )}
          </div>

          <FunnelStatusName
            name={status.name}
            outcome={status.terminalOutcome}
            icon="archive"
            showArchiveTooltip
            showTruncatedNameTooltip
            weight="semibold"
            className={classes(
              'truncate tracking-tight',
              isAiLinked ? 'text-indigo-950' : 'text-secondary-700'
            )}
          />

          <CountBadge
            count={count}
            loading={countLoading}
            size="md"
            variant="secondary"
          />
        </div>

        <div className="flex items-center gap-2">
          {menuItems.length > 0 && (
            <MenuDropdown
              items={menuItems}
              size="sm"
              variant="secondary"
              appearance="ghost"
              handlerComponent={
                <Button
                  variant="secondary"
                  appearance="ghost"
                  size="sm"
                  isIconOnly
                  aria-label={t('communications.pipeline.actions.columnMenu')}
                  disabled={loadingAutomationPerm}
                >
                  <PiDotsThreeVertical className="w-5 h-5 text-secondary-500" />
                </Button>
              }
            />
          )}
        </div>
      </div>

      {canCreateAutomation && (
        <CreateAutomationFromTemplateModal
          isOpen={isAutomationModalOpen}
          onClose={() => setAutomationModalOpen(false)}
          onCreated={() => setAutomationModalOpen(false)}
          title={t('communications.pipeline.actions.quickAutomationTitle')}
          confirmLabel={t(
            'communications.pipeline.actions.quickAutomationConfirm'
          )}
          quickAutomationContext={quickAutomationContext}
          presetMapping={presetMapping}
        />
      )}
    </>
  );
};
